import { Doctor } from "./doctor.definition";
import { Secretary } from "./secretary.definition";
import { RoleEnum, UserInfo } from "./user.definition";

// Unified staff type that combines common properties and optional role-specific ones
export type Staff = UserInfo & {
  id: string;
  userId?: string; // The user ID (for API calls that require userId, not doctor/secretary ID)
  activated?: boolean;
  requiresPasswordChange?: boolean;
  // Secretary-specific
  healthcareFacility?: {
    id: string | number;
    name?: string;
    location?: {
      address?: string;
      city?: string;
      state?: string;
      country?: string;
      zip?: string;
    };
  };
  // Doctor-specific properties (optional for secretaries)
  specialties?: Array<{
    id?: string;
    code: string;
    label: string;
  }>;
  providedServices?: Array<{
    code: string;
    price: number;
  }>;
  healthcareFacilities?: Array<{
    id: string | number;
    name?: string;
    location?: {
      address?: string;
      city?: string;
      state?: string;
      country?: string;
      zip?: string;
    };
  }>;
  hospitalName?: string;
  biography?: string;
  diplomas?: string;
  doctorSpokenLanguages?: string[];
  nextAvailability?: string;
  averageRate?: number;
  careerStartYear?: string;
  refNumber?: string;
  locations?: Array<{
    country: string;
    address: string;
    city: string;
    state?: string;
    zip?: string;
    isPrimary?: boolean;
    coordinates?: { lat: number; lng: number };
  }>;
  contactInfo?: {
    email?: string;
    phoneNumber?: string;
    location?: {
      address?: string;
      city?: string;
      state?: string;
      country?: string;
      zip?: string;
      coordinates?: { lat: number; lng: number };
    };
  };
  location?: {
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
  };
};

// Type guard to check if staff is a doctor
export function isDoctor(staff: Staff): staff is Doctor & Staff {
  return (
    staff.roleName === RoleEnum.DOCTOR ||
    Array.isArray(staff.specialties) ||
    Array.isArray(staff.providedServices)
  );
}

// Type guard to check if staff is a secretary
export function isSecretary(staff: Staff): staff is Secretary & Staff {
  return staff.roleName === RoleEnum.SECRETARY;
}

// Create a create payload type for staff
export type CreateStaffPayload = {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  phoneNumber?: string;
  civility: string;
  roleName: RoleEnum.DOCTOR | RoleEnum.SECRETARY;
  dateOfBirth?: string;
  refNumber?: string;
  careerStartYear?: string;
  activated?: boolean;
  healthcareFacilityIds?: number[];
  preferredNotificationMethods?: string[];
  // Doctor-specific fields (optional for secretary)
  specialties?: Array<{
    id?: string;
    code: string;
    label: string;
  }>;
  providedServices?: Array<{
    code: string;
    price: number;
  }>;
  locations?: Array<{
    country: string;
    address: string;
    city: string;
    state?: string;
    zip?: string;
    isPrimary?: boolean;
  }>;
  hospitalName?: string;
  biography?: string;
  diplomas?: string;
  doctorSpokenLanguages?: string[];
  photoUrl?: string;
  contactInfo?: {
    email?: string;
    phoneNumber?: string;
    location?: {
      address?: string;
      city?: string;
      state?: string;
      country?: string;
      zip?: string;
    };
  };
};
